// Token model + parsing for the HTML token editor.
//
// A token is written in a template as:
//     {{ path ( : modifier ( : args )? )? }}
//
//   path       dot-separated, campaign-relative field path (e.g. start_date, panels.number)
//   modifier   optional keyword (e.g. full, comma, table)
//   args       optional comma list, only used by some modifiers (e.g. table columns)
//
// The same regex is intended to be reusable by the (future) PHP renderer.

import rawCatalog from './contract/tokenCatalog.json';
export type TokenType = 'date' | 'number' | 'money' | 'string' | 'collection';

export interface TokenField {
  path: string;
  label: string;
  type: TokenType;
  category: string;
  // Explicit modifier list; when omitted it is derived from `type`.
  modifiers?: string[];
  // For collections: the sub-fields available for projection / table columns.
  itemFields?: TokenField[];
}

export interface ParsedToken {
  path: string;
  modifier?: string;
  args?: string[];
  raw: string;
}

// --- Modifier catalogs -----------------------------------------------------

export const DATE_MODIFIERS = ['full', 'iso', 'year', 'month', 'day', 'dmy', 'weekday', 'weekday_ordinal_month'];
export const MONEY_MODIFIERS = ['currency', 'comma', 'plain', 'percent'];
export const NUMBER_MODIFIERS = ['comma', 'plain', 'percent'];
export const JOIN_MODIFIERS = ['comma', 'linebreak', 'space', 'bullet', 'semicolon'];

// Human labels. The part before " (" is the short label used on pills.
export const MODIFIER_LABELS: Record<string, string> = {
  full: 'Full (12 March 2026)',
  iso: 'ISO (2026-03-12)',
  year: 'Year (2026)',
  month: 'Month (March)',
  day: 'Day (12)',
  dmy: 'DD/MM/YYYY (12/03/2026)',
  weekday: 'Weekday (Wednesday)',
  weekday_ordinal_month: 'Weekday, Ordinal Month (Saturday 16th May 2026)',
  image: 'First image',
  images: 'All images',
  currency: 'Currency (£1,234.00)',
  comma: 'Thousands (1,234)',
  plain: 'Plain (1234)',
  percent: 'Percent (12%)',
};

export const JOIN_LABELS: Record<string, string> = {
  comma: 'Comma-separated',
  linebreak: 'Line breaks',
  space: 'Space-separated',
  bullet: 'Bulleted',
  semicolon: 'Semicolon-separated',
};

export function isJoinModifier(modifier: string): boolean {
  return JOIN_MODIFIERS.includes(modifier);
}

export function modifiersFor(field: TokenField): string[] {
  if (field.modifiers) return field.modifiers;
  switch (field.type) {
    case 'date':
      return DATE_MODIFIERS;
    case 'money':
      return MONEY_MODIFIERS;
    case 'number':
      return NUMBER_MODIFIERS;
    case 'collection':
      return [...JOIN_MODIFIERS, 'table'];
    default:
      return [];
  }
}

export function defaultModifier(field: TokenField): string | null {
  switch (field.type) {
    case 'date':
      return 'full';
    case 'money':
      return 'currency';
    case 'number':
      return 'comma';
    case 'collection':
      return 'comma';
    default:
      return null;
  }
}

// True when the token has a meaningful display choice (scalar with modifiers,
// or collection). A scalar with no modifiers has exactly one display option.
export function hasDisplayOptions(field: TokenField): boolean {
  if (field.type === 'collection') return true;
  return modifiersFor(field).length > 0;
}

// --- Campaign field catalog ------------------------------------------------

// Shared with the BE contract renderer (diabolical app/Diabolical/ContractTokens.php),
// which reads the same catalog at render time from the deployed FE build
// (dist/tokenCatalog.json -> public/static/js/, emitted by the webpack copy step).
// Edit the JSON, not a copy.

export const CAMPAIGN_TOKEN_FIELDS: TokenField[] = rawCatalog as unknown as TokenField[];

// --- Parsing ---------------------------------------------------------------

// Matches {{ path (: modifier (: args)?)? }}.
//   Group 1 = path, Group 2 = modifier (optional), Group 3 = args string (optional).
export const TOKEN_REGEX = /\{\{([^\{\}:]+)(?::([^\{\}:]+)(?::([^\{\}]+))?)?\}\}/g;

export function parseToken(inner: string): ParsedToken {
  const parts = inner.split(':');
  const path = parts[0].trim();
  const modifier = parts.length > 1 ? parts[1].trim() : undefined;
  const args =
    parts.length > 2
      ? parts
          .slice(2)
          .join(':')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined;
  return { path, modifier, args, raw: inner };
}

export interface TokenMatch {
  token: ParsedToken;
  start: number;
  end: number;
}

export function extractTokens(template: string): TokenMatch[] {
  const results: TokenMatch[] = [];
  const re = new RegExp(TOKEN_REGEX.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(template)) !== null) {
    const inner = m[0].slice(2, -2);
    results.push({ token: parseToken(inner), start: m.index, end: m.index + m[0].length });
  }
  return results;
}

// Resolves a (possibly projected) path against the catalog.
//   'start_date'      -> { field: start_date }
//   'panels.number'   -> { field: panels, itemPath: 'number' }
export function resolvePath(
  fields: TokenField[],
  path: string
): { field: TokenField; itemPath?: string } | undefined {
  const exact = fields.find((f) => f.path === path);
  if (exact) return { field: exact };
  for (const f of fields) {
    if (f.type === 'collection' && path.startsWith(f.path + '.')) {
      return { field: f, itemPath: path.slice(f.path.length + 1) };
    }
  }
  return undefined;
}

export function findItemField(field: TokenField | undefined, itemPath: string): TokenField | undefined {
  return field?.itemFields?.find((f) => f.path === itemPath);
}

// --- Label derivation ------------------------------------------------------

function shortModifierLabel(modifier: string): string {
  const full = MODIFIER_LABELS[modifier];
  if (full) return full.split(' (')[0];
  return modifier;
}

// Builds a human-readable label for a token's inner form, e.g.
//   'start_date:month'            -> 'Start Date · Month'
//   'panels.number:comma'         -> 'Panels Number (Comma-separated)'
//   'panels:table:number,site.name' -> 'Panels table (Number, Site Name)'
export function labelForToken(inner: string, fields: TokenField[]): string {
  const { path, modifier, args } = parseToken(inner);
  const resolved = resolvePath(fields, path);
  const field = resolved?.field;
  const base = field ? field.label : path;

  if (!modifier) return base;

  if (modifier === 'table') {
    const cols = (args || []).map((a) => findItemField(field, a)?.label ?? a);
    return `${base} table (${cols.join(', ')})`;
  }

  if (isJoinModifier(modifier) && field?.type === 'collection') {
    const item = resolved?.itemPath ? findItemField(field, resolved.itemPath) : undefined;
    const subject = item ? `${base} ${item.label}` : base;
    return `${subject} (${JOIN_LABELS[modifier] ?? modifier})`;
  }

  const item = resolved?.itemPath ? findItemField(field, resolved.itemPath) : undefined;
  const subject = item ? `${base} ${item.label}` : base;
  return `${subject} · ${shortModifierLabel(modifier)}`;
}

// Builds the inner token form from its parts. Args are carried by the table
// modifier (column list) and the artwork image modifiers (selection grammar).
export function buildTokenInner(path: string, modifier?: string, args?: string[]): string {
  let inner = path;
  if (modifier) inner += `:${modifier}`;
  if ((modifier === 'table' || modifier === 'image' || modifier === 'images') && args && args.length) inner += `:${args.join(',')}`;
  return inner;
}
